import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '@/features/auth/api';
import { clearAuthData, getAuthToken, getUser, saveAuthData } from '@/core/auth';
import { useSession } from '@/store/session';

type PrefillData = {
  name?: string;
  email?: string;
  mobile?: string;
  birthdate?: string;
};

function normalizeBase64(input: string): string {
  const decodedUri = decodeURIComponent(input);
  const normalized = decodedUri.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '');
  const pad = normalized.length % 4;
  if (pad === 0) return normalized;
  return normalized + '='.repeat(4 - pad);
}

function base64ToJson(input: string): any | null {
  try {
    const normalized = normalizeBase64(input);
    const binary = atob(normalized);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function sanitizePrefill(raw: any): PrefillData | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const email = typeof raw.email === 'string' ? raw.email.trim().toLowerCase() : '';
  const mobile = typeof raw.mobile === 'string' ? raw.mobile.trim() : '';
  const birthdate = typeof raw.birthdate === 'string' ? raw.birthdate.trim() : '';
  return {
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(mobile ? { mobile } : {}),
    ...(birthdate ? { birthdate } : {}),
  };
}

function normalizeUser(userRes: any) {
  return {
    id: Number(userRes.id),
    full_name: userRes.full_name ?? userRes.fullName ?? '',
    email: userRes.email ?? '',
    role_id: userRes.role_id ?? userRes.roleId ?? 3,
    role_name: userRes.role_name ?? userRes.roleName ?? 'customer',
    birth_date: userRes.birth_date ?? userRes.birthDate ?? undefined,
    phone: userRes.phone ?? undefined,
    is_active: userRes.is_active ?? userRes.isActive ?? true,
  };
}

export default function WebappEntryPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { setSession, clearSession } = useSession();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const animalId = Number(params.animalId);
      if (!animalId || Number.isNaN(animalId)) {
        navigate('/adopt', { replace: true });
        return;
      }

      const rawEncoded = [params.encodedData, params['*'], searchParams.get('data')]
        .filter((part): part is string => Boolean(part))
        .map((part) => String(part))
        .join('/');

      const parsed = rawEncoded ? sanitizePrefill(base64ToJson(rawEncoded)) : null;
      const stateBase = { animalId };

      // 1) Sessão ativa
      const storedToken = getAuthToken();
      const storedUser = getUser();
      if (storedToken && storedUser) {
        try {
          const me = await authApi.me();
          if (cancelled) return;
          saveAuthData(storedToken, me);
          setSession(storedToken, me);
          navigate('/adoption/wizard', { replace: true, state: stateBase });
          return;
        } catch {
          clearAuthData();
          clearSession();
        }
      }

      // 2) Sem sessão, com base64 e email
      if (parsed?.email) {
        try {
          const auto = await authApi.autoLoginByEmail({ email: parsed.email });
          if (cancelled) return;

          if (auto?.exists && auto?.loggedIn && auto?.token && auto?.user) {
            const normalizedUser = normalizeUser(auto.user);
            saveAuthData(auto.token, normalizedUser as any);
            setSession(auto.token, normalizedUser as any);
            navigate('/adoption/wizard', { replace: true, state: stateBase });
            return;
          }

          if (auto?.exists && !auto?.loggedIn) {
            navigate('/login', {
              replace: true,
              state: {
                returnTo: '/adoption/wizard',
                ...stateBase,
              },
            });
            return;
          }
        } catch {
          // fallback para signup abaixo
        }
      }

      // 3) Sem sessão + sem base64 (ou email não existente)
      navigate('/signup', {
        replace: true,
        state: {
          returnTo: '/adoption/wizard',
          ...stateBase,
          prefill: parsed
            ? {
                fullName: parsed.name ?? '',
                email: parsed.email ?? '',
                phone: parsed.mobile ?? '',
                birthDate: parsed.birthdate ?? '',
              }
            : undefined,
        },
      });
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [navigate, params, searchParams, setSession, clearSession]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Procesando acceso...</span>
      </div>
    </div>
  );
}
